import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RetailerComparison } from './types';

interface RetailerComparisonTabProps {
  retailerComparison: RetailerComparison;
}

export default function RetailerComparisonTab({
  retailerComparison,
}: RetailerComparisonTabProps) {
  if (!retailerComparison) {
    return <p className="text-gray-500">No retailer data available.</p>;
  }

  const comparison = retailerComparison;
  if (comparison.retailers.length === 0) {
    return <p className="text-gray-500">No retailer comparison data available.</p>;
  }

  // Prepare chart data for all retailers
  const allDates = new Set<number>();
  comparison.retailers.forEach(retailer => {
    retailer.weeklyData.forEach(week => {
      allDates.add(week.date);
    });
  });

  const sortedDates = Array.from(allDates).sort((a, b) => a - b);
  const chartData = sortedDates.map(date => {
    const dataPoint: any = {
      date,
      dateFormatted: new Date(date * 1000).toISOString().split('T')[0],
    };

    comparison.retailers.forEach(retailer => {
      const weekData = retailer.weeklyData.find(w => w.date === date);
      if (weekData) {
        dataPoint[`${retailer.retailer} (All)`] = parseFloat(weekData.averagePrice.replace('£', ''));
        if (weekData.frequentItemsAvg && weekData.frequentItemsAvg !== '£0.00') {
          dataPoint[`${retailer.retailer} (Frequent)`] = parseFloat(weekData.frequentItemsAvg.replace('£', ''));
        }
      }
    });

    return dataPoint;
  });

  const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="card p-5 md:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Retailers tracked</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{comparison.retailers.length}</p>
        </div>
        <div className="card p-5 md:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Frequent items tracked</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{comparison.frequentProductCount}</p>
          <p className="text-xs text-ink-400 mt-1">Items appearing 2+ times.</p>
        </div>
      </div>

      {chartData.length > 1 && (
        <section className="card space-y-5 p-6">
          <div>
            <h3 className="text-lg font-semibold text-ink-700">Average price trends by retailer</h3>
            <p className="text-sm text-ink-500 mt-1">Compare every grocer&apos;s weekly spend across all items and your frequent staples.</p>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 40, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="dateFormatted"
                angle={-45}
                textAnchor="end"
                height={80}
                style={{ fontSize: '12px' }}
              />
              <YAxis label={{ value: 'Avg price (£)', angle: -90, position: 'insideLeft', dx: -25, dy: 40 }} />
              <Tooltip formatter={(value: any) => `£${parseFloat(value).toFixed(2)}`} />
              <Legend />
              {comparison.retailers.map((retailer, index) => (
                <React.Fragment key={retailer.retailer}>
                  <Line
                    key={`${retailer.retailer} (All)`}
                    type="monotone"
                    dataKey={`${retailer.retailer} (All)`}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    isAnimationActive={false}
                  />
                  {retailer.weeklyData.some((week) => week.frequentItemsAvg && week.frequentItemsAvg !== '£0.00') && (
                    <Line
                      key={`${retailer.retailer} (Frequent)`}
                      type="monotone"
                      dataKey={`${retailer.retailer} (Frequent)`}
                      stroke={colors[index % colors.length]}
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  )}
                </React.Fragment>
              ))}
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      <div
        className={
          comparison.retailers.length === 1
            ? 'w-full'
            : 'grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        }
      >
        {comparison.retailers.map((retailer) => (
          <div
            key={retailer.retailer}
            className={
              comparison.retailers.length === 1
                ? 'card overflow-hidden'
                : 'card overflow-hidden'
            }
          >
            <div className="border-b border-ink-100/70 p-6">
              <h3 className="text-lg font-semibold text-ink-700">{retailer.retailer}</h3>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-ink-400">Min price</p>
                  <p className="mt-1 text-lg font-semibold text-ink-700">{retailer.minPrice}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-400">Max price</p>
                  <p className="mt-1 text-lg font-semibold text-ink-700">{retailer.maxPrice}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-400">Weeks</p>
                  <p className="mt-1 text-lg font-semibold text-ink-700">{retailer.weeklyData.length}</p>
                </div>
              </div>
            </div>
            <div className="max-h-64 divide-y divide-ink-100/80 overflow-y-auto">
              {retailer.weeklyData.map((week) => (
                <div key={week.date} className="p-4 transition hover:bg-ink-50/80">
                  <p className="text-sm font-medium text-ink-700">{week.dateFormatted}</p>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-ink-400">All items avg</p>
                      <p className="text-sm font-semibold text-ink-700">{week.averagePrice}</p>
                      <p className="text-xs text-ink-300">({week.itemsCount} items)</p>
                    </div>
                    {week.frequentItemsAvg !== '£0.00' && (
                      <div>
                        <p className="text-xs text-ink-400">Frequent items avg</p>
                        <p className="text-sm font-semibold text-primary-600">{week.frequentItemsAvg}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

